/**
 * CreditTopUpSection - Credit Package Purchase
 *
 * Displays credit packages for one-time purchases.
 * - Package cards with credits + EUR price
 * - "Beliebt" badge on recommended package
 * - Savings indicator for larger packages
 * - Stripe Checkout integration
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Coins, CreditCard, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Button, Badge, Loading } from '@/components/v2/ui';
import { getCreditTopUpPackages } from '@/lib/api/credits';
import { createStripeCheckout } from '@/lib/api/payment';
import type { CreditTopUpPackage } from '@/types/credits';
import { toast } from 'sonner';
import { formatNumber, formatCurrency } from '@/lib/utils';

const FALLBACK_PACKAGES: CreditTopUpPackage[] = [
  { id: 'pkg-1000', credits: 1000, price_eur: 9.99, savings_percent: null, is_popular: false },
  { id: 'pkg-2500', credits: 2500, price_eur: 22.99, savings_percent: 8, is_popular: true },
  { id: 'pkg-5000', credits: 5000, price_eur: 42.99, savings_percent: 14, is_popular: false },
  { id: 'pkg-10000', credits: 10000, price_eur: 79.99, savings_percent: 20, is_popular: false },
];

interface CreditTopUpSectionProps {
  canManage: boolean;
}

export function CreditTopUpSection({ canManage }: CreditTopUpSectionProps) {
  const { t } = useTranslation();
  const [loadingPackageId, setLoadingPackageId] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['credit-topup-packages'],
    queryFn: getCreditTopUpPackages,
    staleTime: 1000 * 60 * 60,
  });

  if (!canManage) return null;
  const packages = data?.packages ?? FALLBACK_PACKAGES;

  const handlePurchase = async (pkg: CreditTopUpPackage) => {
    setLoadingPackageId(pkg.id);
    try {
      const result = await createStripeCheckout(pkg.price_eur, {
        credits: pkg.credits,
        package_id: pkg.id,
      });
      if (result.success && result.checkout_url) {
        // eslint-disable-next-line react-hooks/immutability -- browser navigation via window.location is intentional
        window.location.href = result.checkout_url;
      } else {
        toast.error(result.error || t('billing.checkoutError'));
        setLoadingPackageId(null);
      }
    } catch {
      toast.error(t('errors.unexpected'));
      setLoadingPackageId(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-6">
        <h4 className="flex items-center justify-center gap-2 text-xl font-semibold text-gray-900 dark:text-gray-50">
          <Coins className="h-6 w-6 text-yellow-500" />
          {t('billing.buyCredits')}
        </h4>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          {t('billing.buyCreditsDescription')}
        </p>
      </div>
      {isLoading ? (
        <Loading className="py-8" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {packages.map((pkg) => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              isLoading={loadingPackageId === pkg.id}
              disabled={loadingPackageId !== null}
              onPurchase={() => handlePurchase(pkg)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface PackageCardProps {
  pkg: CreditTopUpPackage;
  isLoading: boolean;
  disabled: boolean;
  onPurchase: () => void;
}

function PackageCard({ pkg, isLoading, disabled, onPurchase }: PackageCardProps) {
  const { t } = useTranslation();
  const gradientClass = pkg.is_popular
    ? 'bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20'
    : 'bg-gradient-to-br from-gray-50 to-primary-50 dark:from-gray-800/50 dark:to-primary-900/20';
  const ringClass = pkg.is_popular ? 'ring-2 ring-yellow-400 dark:ring-yellow-500' : '';

  return (
    <Card className={`relative ${gradientClass} ${ringClass}`}>
      {pkg.is_popular && (
        <div className="absolute -top-2 left-1/2 -translate-x-1/2">
          <Badge variant="warning" className="px-3 py-1 text-xs font-semibold">{t('billing.popular')}</Badge>
        </div>
      )}
      {pkg.savings_percent && (
        <div className="absolute -top-2 -right-2">
          <Badge variant="success" className="px-2 py-1 text-xs">{t('billing.savingsPercent', { percent: pkg.savings_percent })}</Badge>
        </div>
      )}
      <CardContent className="p-5 text-center">
        <div className="mb-3">
          <Sparkles className="h-8 w-8 mx-auto text-yellow-500 dark:text-yellow-400" />
        </div>
        <div className="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-1">
          {formatNumber(pkg.credits)}
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">Credits</div>
        <div className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
          {formatCurrency(pkg.price_eur)}
        </div>
        <Button
          variant={pkg.is_popular ? 'primary' : 'outline'}
          className="w-full"
          onClick={onPurchase}
          disabled={disabled}
        >
          <CreditCard className="h-4 w-4 mr-2" />
          {isLoading ? t('common.loading') : t('billing.buy')}
        </Button>
      </CardContent>
    </Card>
  );
}
